import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { FormCheckbox, TextInput } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { getAbsenceCategoryTranslationKey } from "@msuite/utils/absences";
import {
	type AbsenceCategoryForm as AbsenceCategoryFormType,
	absenceCategoryForm,
} from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import type { Enums } from "@msuite/utils/types";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { client } from "@/hono";
import { useAbsenceTypes } from "@/hooks/use-absence-types";

/** Props Interface */
interface AbsenceCategoryFormProps {
	category: Enums<"absenceCategory">;
}

async function getDefaultValues(category: Enums<"absenceCategory">) {
	const response = await client.organizations.absences[":category"].types.$get({
		param: { category },
	});
	const data = await responseHandler(response);
	return data.absenceTypes;
}

export function AbsenceCategoryForm({ category }: AbsenceCategoryFormProps) {
	/** State */
	const [addedIds, setAddedIds] = useState<string[]>([]);

	const { refetch } = useAbsenceTypes(category);

	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(absenceCategoryForm),
		defaultValues: async () => {
			return await getDefaultValues(category);
		},
	});
	const fieldArray = useFieldArray({ control: methods.control, name: "types", keyName: "_id" });

	/** Functions */
	function handleOnAdd() {
		const id = generateId();
		setAddedIds((prev) => prev.concat([id]));
		fieldArray.append({ id, name: "" });
	}

	async function handleOnSubmit(json: AbsenceCategoryFormType) {
		await client.organizations.absences[":category"].types.$patch({ json, param: { category } });
		setAddedIds([]);
		const newValues = await getDefaultValues(category);
		methods.reset(newValues);
		await refetch();
		toast.success(t("general.successfully_updated"));
	}

	/** Render */
	return (
		<form
			className="v-stack"
			onSubmit={methods.handleSubmit(handleOnSubmit)}
		>
			<p>{t(getAbsenceCategoryTranslationKey(category))}</p>
			{fieldArray.fields.length > 0 && <Divider variant="card" />}
			<div className="v-stack gap-4">
				{fieldArray.fields.map((field, index) => (
					<div
						className="v-stack card"
						key={field.id}
					>
						<div className="h-stack items-end">
							<TextInput
								control={methods.control}
								name={`types.${index}.name`}
								label={t("general.name")}
								isRequired
								placeholder={t("absences.absence_type")}
							/>
							{addedIds.includes(field.id) && (
								<Button
									variant="danger-ghost"
									onClick={() => fieldArray.remove(index)}
									size="sm"
									icon={
										<Icon
											name="Trash"
											color="var(--color-red-500)"
										/>
									}
								/>
							)}
						</div>
						<Divider variant="card" />
						<div className="v-stack w-full">
							<FormCheckbox
								control={methods.control}
								name={`types.${index}.requiresDocument`}
							>
								{t("absences.requires_document")}
							</FormCheckbox>
							<FormCheckbox
								control={methods.control}
								name={`types.${index}.requiresApproval`}
							>
								{t("absences.requires_approval")}
							</FormCheckbox>
							<FormCheckbox
								control={methods.control}
								name={`types.${index}.deductsFromBalance`}
							>
								{t("absences.deducts_from_balance")}
							</FormCheckbox>
						</div>
					</div>
				))}
			</div>
			<div className="h-stack justify-end">
				<Button
					onClick={handleOnAdd}
					icon={<Icon name="Plus" />}
				>
					{t("general.add")}
				</Button>
				<Button
					type="submit"
					variant="primary"
					isLoading={methods.formState.isSubmitting}
				>
					{t("general.submit")}
				</Button>
			</div>
			<Divider variant="card" />
		</form>
	);
}
